import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export interface PosDevice {
  id: string;
  posDeviceId: string;
  tenantId: string;
  locationId: string | null;
  registerId: string | null;
  deviceName: string | null;
  isActive: string; // 'true' | 'false'
  lastSeenAt: string | null;
  syncedAt: string;
}

export interface PosTenantConfig {
  id: string;
  tenantId: string;
  locationId: string;
  storeName: string | null;
  storePhone: string | null;
  storeAddress: string | null;
  city: string | null;
  state: string | null;
  currency: string | null;
  currencySymbol: string | null;
  salesTaxRate: string | null;
  customFee1Label: string | null;
  customFee1Rate: string | null;
  customFee2Label: string | null;
  customFee2Rate: string | null;
  receiptHeader: string | null;
  receiptFooter: string | null;
  primaryColorHex: string | null;
  accentColorHex: string | null;
  secondaryColorHex: string | null;
  enabledFeatures: Record<string, unknown> | null;
  customerDisplayRewardsEnabled: boolean | null;
  /** Member greeting on the customer display ({firstName}/{storeName}); null = the default. */
  customerDisplayGreeting: string | null;
  allowPriceOverride: boolean | null;
  /** Per-location receipt-delivery toggles — show Text/Email in the POS
   *  post-payment receipt prompt. */
  receiptSmsEnabled: boolean | null;
  receiptEmailEnabled: boolean | null;
  /** Store Config switch for the receipt logo — the only control the POS
   *  reads. null = never set, which the POS treats as OFF. */
  showLogoOnReceiptDashboardOverride: boolean | null;
  /** Tri-state tip override: true/false force the customer-display tip prompt
   *  on/off for this location, null = inherit the POS-local tip toggle. */
  tipEnabledDashboardOverride: boolean | null;
  /** Comma-separated tip % presets for the CDS tip prompt (e.g. '10,20,30'). */
  tipPresets: string | null;
  /** This location's receipt logo (S3 URL). Managed via useLocationLogo("receipt-logo"). */
  receiptLogoUrl: string | null;
  /** This location's customer-display logo (S3 URL). Managed via useLocationLogo("cds-logo"). */
  cdsLogoUrl: string | null;
  rawConfig: Record<string, unknown> | null;
  syncedAt: string;
}

export interface PosRolePermission {
  id: string;
  posPermissionId: string;
  tenantId: string;
  locationId: string;
  role: string;
  canProcessOrders: boolean;
  canApplyDiscount: boolean;
  canOverridePrice: boolean;
  canVoidItem: boolean;
  canProcessRefund: boolean;
  canApproveRefund: boolean;
  canOpenDrawer: boolean;
  canCashInOut: boolean;
  canViewTransactions: boolean;
  canVoidTransaction: boolean;
  canViewReports: boolean;
  canChangeSettings: boolean;
  canManageEmployees: boolean;
  canAddProduct: boolean;
  canUpdateApp: boolean;
  canIssueStoreCredit: boolean;
  canRedeemStoreCredit: boolean;
  canShareStoreCreditLink: boolean;
  canSellGiftCard: boolean;
  syncedAt: string;
}

export function usePosDevices(locationId?: string) {
  return useQuery<{ data: PosDevice[] }>({
    queryKey: ['pos-devices', locationId ?? 'all'],
    queryFn: () => {
      const qs = locationId ? `?location_id=${encodeURIComponent(locationId)}` : '';
      return api.get(`/pos/devices${qs}`);
    },
  });
}

/**
 * Admin-initiated deregister + delete of a POS register row. Forwards to
 * pos-backend which revokes the device's auth credentials and deletes the
 * canonical pos.registers row, then wipes the dashboard's pos_devices
 * mirror. After this returns, the register is gone from:
 *   - The Registers tab in this dashboard (immediate)
 *   - The POS app's registration picker (next time it lists registers)
 *   - The device's ability to mint/refresh tokens (immediate)
 *
 * Admin role required server-side. Invalidates `pos-devices` queries on
 * success so the table updates without a manual refresh.
 */
export function useDeletePosRegister() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ registerId }: { registerId: string }) =>
      api.delete<{
        success: boolean;
        posBackendOk: boolean;
        posBackendError?: string;
        dashboardRowsDeleted: number;
      }>(`/pos/registers/${registerId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pos-devices'] });
    },
  });
}

export function usePosTenantConfig(locationId?: string) {
  return useQuery<{ data: PosTenantConfig | null }>({
    queryKey: ['pos-tenant-config', locationId ?? ''],
    queryFn: () => api.get(`/pos/tenant-config?location_id=${encodeURIComponent(locationId ?? '')}`),
    enabled: !!locationId,
  });
}

export function usePosRolePermissions(locationId?: string) {
  return useQuery<{ data: PosRolePermission[] }>({
    queryKey: ['pos-role-permissions', locationId ?? ''],
    queryFn: () => api.get(`/pos/role-permissions?location_id=${encodeURIComponent(locationId ?? '')}`),
    enabled: !!locationId,
  });
}

export function useUpdatePosTenantConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { locationId: string } & Record<string, unknown>) => api.put('/pos/tenant-config', payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pos-tenant-config', variables.locationId] });
    },
  });
}

export function useUpdatePosRolePermissions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { locationId: string; permissions: PosRolePermission[] }) =>
      api.put('/pos/role-permissions', args),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pos-role-permissions', variables.locationId] });
    },
  });
}

// ── Clock Hours ──

export interface ClockHoursSession {
  clockIn: string;
  clockOut: string | null;
  minutes: number;
  status: 'completed' | 'live' | 'orphaned';
}

export interface ClockHoursEntry {
  employeeId: string;
  totalHours: number;
  totalMinutes: number;
  liveCount: number;
  orphanedCount: number;
  sessions: ClockHoursSession[];
}

export function usePosClockHours(locationId?: string, since?: string) {
  return useQuery<{ data: ClockHoursEntry[] }>({
    queryKey: ['pos-clock-hours', locationId ?? '', since ?? ''],
    queryFn: () => {
      const params = new URLSearchParams();
      if (locationId) params.set('location_id', locationId);
      if (since) params.set('since', since);
      return api.get(`/pos/clock-hours?${params.toString()}`);
    },
  });
}

// ── Cash Variance ──

export interface CashVarianceEntry {
  id: string;
  registerId: string | null;
  locationId: string | null;
  employeeId: string;
  countedAmount: number;
  variance: number;
  status: 'balanced' | 'over' | 'short';
  closedAt: string;
  note: string | null;
}

export function usePosCashVariance(locationId?: string, since?: string) {
  return useQuery<{ data: CashVarianceEntry[] }>({
    queryKey: ['pos-cash-variance', locationId ?? '', since ?? ''],
    queryFn: () => {
      const params = new URLSearchParams();
      if (locationId) params.set('location_id', locationId);
      if (since) params.set('since', since);
      return api.get(`/pos/cash-variance?${params.toString()}`);
    },
  });
}

// ── Products POS Management config (per-location, via products service) ──

import { productsApi } from '@/lib/api-client';
import { useAuthStore } from '@/stores/auth-store';
import { useLocationStore } from '@/stores/location-store';

export const DEFAULT_COST_VISIBLE_ROLES = ['super_admin', 'owner', 'admin'] as const;

export interface ProductsPosConfig {
  locationId: string;
  costVisibleRoles: string[];
  /** Per-location Customer Engagement toggle. When true the dashboard's
   *  Customer Engagement tab is visible at this location. Super-admin only.
   *  Replaces the older build-time VITE_CUSTOMER_ENGAGEMENT_ENABLED flag. */
  customerEngagementEnabled: boolean;
  /** Per-location Promotions toggle. When true the Promotions tab (inside
   *  POS Management) and the Promotions sub-tab (inside Reports → POS) are
   *  visible at this location. Super-admin only. Mirrors customerEngagement. */
  promotionsEnabled: boolean;
  /** Per-location Store Credit switch: refunds to store credit, the Store
   *  Credit tender and gift cards work here only while it's on. Super-admin
   *  only, default off; the owner's Store Credit toggle is hidden until then. */
  storeCreditEnabled: boolean;
  /** Per-location Brand Colors permission. When true (the default) the
   *  merchant can edit Store Config → Brand Colors and the saved colors
   *  flow to the POS app on next push. When false the section is hidden
   *  on the dashboard and pos-backend nulls the color hexes downstream so
   *  the POS app falls back to its default theme. Super-admin only.
   *  Inverts the polarity of the other two toggles: brand colors are an
   *  already-shipped surface, so we default to "allowed". */
  brandColorsEnabled: boolean;
  /** Per-location SMS marketing-campaign sender (E.164). Legacy single value,
   *  kept in sync to campaignSmsNumbers[0]. Super-admin only — distinct from
   *  the transactional sender which lives on locations.sms_phone_number. */
  campaignSmsNumber: string | null;
  /** Per-location pool of campaign sender numbers (E.164). The pos-backend
   *  campaign worker round-robins recipients across the pool via Telnyx for
   *  throughput. Empty → no campaign sender assigned; scheduling a campaign
   *  at this location is rejected with a contact-support error. */
  campaignSmsNumbers: string[];
  /** Raw per-location mobile permissions. Edited by the Mobile Config tab;
   *  the phone never sees this shape — it reads the resolved allow-list from
   *  the products service's GET /mobile-config instead, so the role hierarchy
   *  is not reimplemented on the client. `{}` means "all defaults", which is
   *  how every location starts. */
  mobileConfig: MobileConfig;
  updatedAt: string | null;
}

// ── Mobile app permissions (per-location, via products service) ──

/** Roles, ordered high to low. Mirrors the products service hierarchy. */
export type MobileRole = 'super_admin' | 'owner' | 'admin' | 'manager' | 'employee';

/** One page's override. `roles` is the complete set allowed at this location —
 *  omitting it inherits the build's default. `enabled: false` hides the page
 *  from everyone here. Access is an explicit set of roles rather than a
 *  "minimum role and above" floor, so a store can allow manager but not admin.
 */
export interface MobilePageRule {
  roles?: MobileRole[];
  enabled?: boolean;
}

export interface MobileConfig {
  pages?: Record<string, MobilePageRule>;
  fields?: Record<string, MobileRole[]>;
  /** Who may edit each section of the app's Find Product → Details tab. */
  edit?: Record<string, MobileRole[]>;
}

/** The pages and fields the installed mobile build knows about. Served by the
 *  products service so the editor cannot offer a page the app has no route
 *  for, and so a new page appears here without a dashboard release.
 *
 *  `defaultRoles: null` means the page ships open to every signed-in user.
 *  `roles` is the configurable set — super_admin is absent because platform
 *  admins are never gated by a merchant's configuration. */
export interface MobileConfigCatalogue {
  pages: { key: string; defaultRoles: MobileRole[] | null }[];
  fields: string[];
  /** Editable Details-tab sections. Optional: a products service older than
   *  the section-edit release doesn't send it. */
  editSections?: { key: string; defaultRoles: MobileRole[] }[];
  roles: MobileRole[];
}

export function useMobileConfigCatalogue() {
  return useQuery<MobileConfigCatalogue>({
    queryKey: ['mobile-config-catalogue'],
    queryFn: () => productsApi.get<MobileConfigCatalogue>('/mobile-config/catalogue'),
    // Describes the deployed build, not a location — no need to refetch per
    // location or on window focus.
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateMobileConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ locationId, mobileConfig }: { locationId: string; mobileConfig: MobileConfig }) =>
      productsApi.put<{ locationId: string; mobileConfig: MobileConfig; updatedAt: string }>(
        `/locations/${locationId}/pos-config/mobile-config`,
        { mobileConfig },
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products-pos-config', variables.locationId] });
    },
  });
}

/** Fetch the per-location cost-visibility config from the products service.
 *  `tenantId` is optional — pass it when calling cross-tenant from the
 *  super-admin tenant detail page; merchant-side callers leave it undefined
 *  and the products service resolves the tenant from the JWT. */
export function useProductsPosConfig(locationId?: string | null, tenantId?: string) {
  return useQuery<ProductsPosConfig>({
    queryKey: ['products-pos-config', locationId ?? '', tenantId ?? ''],
    queryFn: () => productsApi.get<ProductsPosConfig>(`/locations/${locationId}/pos-config`, undefined, tenantId),
    enabled: !!locationId,
  });
}

export function useUpdateProductsPosConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ locationId, costVisibleRoles }: { locationId: string; costVisibleRoles: string[] }) =>
      productsApi.put<ProductsPosConfig>(`/locations/${locationId}/pos-config`, { costVisibleRoles }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products-pos-config', variables.locationId] });
    },
  });
}

/**
 * Flip the per-location Customer Engagement toggle. Super-admin only — the
 * backend route requires role >= super_admin. The dashboard hides the
 * settings UI for non-super-admin users so this hook is only callable from
 * authorised places.
 */
export function useUpdateCustomerEngagementEnabled(tenantId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ locationId, enabled }: { locationId: string; enabled: boolean }) =>
      productsApi.put<{ locationId: string; customerEngagementEnabled: boolean; updatedAt: string }>(
        `/locations/${locationId}/pos-config/customer-engagement`,
        { enabled },
        tenantId,
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products-pos-config', variables.locationId] });
    },
  });
}

/**
 * Flip the per-location Promotions toggle. Super-admin only — the backend
 * route requires role >= super_admin. Mirrors useUpdateCustomerEngagementEnabled.
 * When the value flips, both the Promotions tab inside POS Management AND
 * the Promotions sub-tab inside Reports → POS appear/disappear at the
 * matching location on the next refetch.
 */
export function useUpdatePromotionsEnabled(tenantId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ locationId, enabled }: { locationId: string; enabled: boolean }) =>
      productsApi.put<{ locationId: string; promotionsEnabled: boolean; updatedAt: string }>(
        `/locations/${locationId}/pos-config/promotions`,
        { enabled },
        tenantId,
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products-pos-config', variables.locationId] });
    },
  });
}

/**
 * Flip the per-location Store Credit switch. Super-admin only — the backend
 * route requires role >= super_admin. Mirrors useUpdatePromotionsEnabled.
 * pos-backend reads the same column, so registers at the location pick the
 * change up on their next sync.
 */
export function useUpdateStoreCreditEnabled(tenantId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ locationId, enabled }: { locationId: string; enabled: boolean }) =>
      productsApi.put<{ locationId: string; storeCreditEnabled: boolean; updatedAt: string }>(
        `/locations/${locationId}/pos-config/store-credit`,
        { enabled },
        tenantId,
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products-pos-config', variables.locationId] });
    },
  });
}

/**
 * Flip the per-location Brand Colors permission. Super-admin only — the
 * backend route requires role >= super_admin. Two writes happen back-to-back:
 *
 *   1. PUT to the products service — flips the gate that the dashboard reads
 *      to decide whether to render the merchant Brand Colors block.
 *   2. PUT to /pos/tenant-config — forwards just `brand_colors_enabled` to
 *      pos-backend so the next /sync/download to POS devices honors the
 *      gate (nulls the color hexes when false). This second write is the
 *      one that makes the change visible on cashier registers; without it
 *      pos-backend keeps serving the merchant's saved colors.
 *
 * The pos-backend write is best-effort — if POS_BACKEND_URL isn't set or
 * the network call fails, the dashboard side still works. The dashboard's
 * /pos/tenant-config endpoint surfaces this via `posPushOk`.
 *
 * The merchant's saved color hexes stay in the products row when the gate
 * flips off, so flipping it back on restores the same colors instantly.
 */
export function useUpdateBrandColorsEnabled(tenantId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ locationId, enabled }: { locationId: string; enabled: boolean }) => {
      const productsResp = await productsApi.put<{
        locationId: string;
        brandColorsEnabled: boolean;
        updatedAt: string;
      }>(`/locations/${locationId}/pos-config/brand-colors`, { enabled }, tenantId);

      // Forward to pos-backend via the dashboard's existing tenant-config
      // proxy. Threads `tenantId` so super-admin cross-tenant calls carry
      // the X-Tenant-Id header the dashboard's tenant guard expects — without
      // it the JWT's super-admin tenant would mismatch the target location's
      // tenant and the write returns 403. Failures don't unwind the gate
      // change — the merchant dashboard already reflects the new state;
      // only POS registers lag until the next push or manual retry.
      const posPushResp = await api
        .put<{ success: boolean; posPushOk?: boolean }>(
          '/pos/tenant-config',
          {
            location_id: locationId,
            brand_colors_enabled: enabled,
          },
          tenantId,
        )
        .catch(() => ({ success: false, posPushOk: false }));

      return { ...productsResp, posPushOk: posPushResp?.posPushOk ?? false };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products-pos-config', variables.locationId] });
    },
  });
}

/**
 * Set the per-location SMS marketing-campaign sender override. Super-admin
 * only. Accepts E.164 / 10-digit (server normalizes) or null to clear and
 * fall back to the platform default (NIB Pleasanton +19254682671). Mirrors
 * the other super-admin mutations — threads `tenantId` so cross-tenant
 * calls from the AdminTenantDetailPage carry X-Tenant-Id.
 */
export function useUpdateCampaignSmsNumber(tenantId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ locationId, campaignSmsNumber }: { locationId: string; campaignSmsNumber: string | null }) =>
      productsApi.put<{ locationId: string; campaignSmsNumber: string | null; updatedAt: string }>(
        `/locations/${locationId}/pos-config/campaign-sms-number`,
        { campaignSmsNumber },
        tenantId,
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products-pos-config', variables.locationId] });
    },
  });
}

/**
 * Set the per-location pool of campaign sender numbers (super-admin only).
 * The pos-backend campaign worker round-robins recipients across the pool via
 * Telnyx. Server normalizes (E.164 / 10-digit) and dedups; an empty array
 * clears the pool. Hits PUT /locations/:id/pos-config/campaign-sms-numbers.
 */
export function useUpdateCampaignSmsNumbers(tenantId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ locationId, campaignSmsNumbers }: { locationId: string; campaignSmsNumbers: string[] }) =>
      productsApi.put<{
        locationId: string;
        campaignSmsNumber: string | null;
        campaignSmsNumbers: string[];
        updatedAt: string;
      }>(`/locations/${locationId}/pos-config/campaign-sms-numbers`, { campaignSmsNumbers }, tenantId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products-pos-config', variables.locationId] });
    },
  });
}

/** Hook returning whether the active user can see cost / vendor price at the
 *  currently-selected location. Falls back to the historic default while the
 *  query is in flight or no row exists yet. */
export function useCanSeeCost(): boolean {
  const { user } = useAuthStore();
  const { selectedLocationId } = useLocationStore();
  const { data } = useProductsPosConfig(selectedLocationId);
  const allowed =
    data?.costVisibleRoles && data.costVisibleRoles.length > 0 ? data.costVisibleRoles : DEFAULT_COST_VISIBLE_ROLES;
  return (allowed as readonly string[]).includes(user?.role || '');
}
